import {
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysSupplier } from '../queryKeys';

import { fetchSupplierResponsiblePersonOptions } from './fetchSupplierResponsiblePersonOptions';
import { type SupplierResponsiblePersonOptionsResponse } from './types';

/**
 * Generates the query options for the supplier responsible-person filter
 * options query so they can be shared between the useQuery hook and
 * imperative fetching (e.g. via SelectServerDriven's createLoadOptions).
 *
 * @param recipientCompanyIds - The recipient company IDs to scope the options to.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and staleTime.
 */
export const getSupplierResponsiblePersonOptionsQueryOptions = <
  TData = SupplierResponsiblePersonOptionsResponse,
>({
  recipientCompanyIds,
  options,
}: {
  recipientCompanyIds: readonly UUID[] | undefined;
  options?: Omit<
    UseQueryOptions<SupplierResponsiblePersonOptionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: recipientCompanyIds?.length
      ? async ({ signal }: QueryFunctionContext) =>
          fetchSupplierResponsiblePersonOptions(
            { recipientCompanyIds: [...recipientCompanyIds] },
            { signal },
          )
      : skipToken,
    queryKey: queryKeysSupplier.getResponsiblePersonOptions({
      recipientCompanyIds: recipientCompanyIds
        ? [...recipientCompanyIds]
        : undefined,
    }),
    staleTime: ms('60s'),
    ...options,
  });

/**
 * React Query based custom hook for getting the people selectable in the
 * supplier overview's responsible-person filter: everyone owning at least
 * one supplier relationship of the given recipient companies.
 *
 * @param recipientCompanyIds - The recipient company IDs to scope the options to.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Suppliers/operation/get_supplier_responsible_person_options_suppliers_responsible_persons_get
 */
export const useQuerySupplierResponsiblePersonOptions = <
  TData = SupplierResponsiblePersonOptionsResponse,
>(
  recipientCompanyIds: readonly UUID[] | undefined,
  options?: Omit<
    UseQueryOptions<SupplierResponsiblePersonOptionsResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getSupplierResponsiblePersonOptionsQueryOptions<TData>({
      options,
      recipientCompanyIds,
    }),
  );
